import React, { useEffect, useMemo, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import { useShallow } from 'zustand/react/shallow'
import { useCrmReportsStore } from '../stores/crmReportsStore'
import { useUserContext } from '../../../contexts/UserContext'
import { useIsMobile } from '../hooks/useIsMobile'
import { useOpenPerson } from '../hooks/useOpenPerson'
import { usePageTour } from '../hooks/usePageTour'
import { ownerColor } from '../PipelinePage/OpportunityCard'
import { parseDateOnly } from '../PersonDetailPage/vehicleToQuote'
import { ReportsProfitPopover } from './ReportsProfitPopover'
import {
    SCOPES, periodOptions, periodLabel as labelForPeriod, scopePatch, selectedKey,
} from './reportPeriods'
import './ReportsPage.scss'

// The Reports tour (see usePageTour). Canonical order; a step whose target isn't
// on screen (a member, a one-user account) is left out of a run.
const REPORTS_TOUR_VERSION = 1
const REPORTS_TOUR_STEPS = [
    {
        element: '.reports-page .kpis',
        data: { id: 'summary' },
        popover: {
            title: 'Your numbers',
            description: 'Deals closed, gross profit, and what’s collected or still outstanding for the period. Click the Outstanding amount to list only deals still owed.',
        },
    },
    {
        element: '.reports-page .rp-period',
        data: { id: 'period' },
        popover: {
            title: 'Choose a period',
            description: 'Pick a scope (month, quarter, year to date, year or all time), then which one.',
        },
    },
    {
        element: '.reports-page .rp-owners',
        data: { id: 'salesperson' },
        popover: {
            title: 'Choose a salesperson',
            description: 'See the whole company or one salesperson’s numbers.',
        },
    },
    {
        // The head, not the panel: always there, and a full table outgrows the screen.
        element: '.reports-page .deals-head',
        data: { id: 'deals' },
        popover: {
            title: 'Deals closed',
            description: 'Every sale in the period. Select a customer to open them, or a profit amount to see its breakdown.',
        },
    },
    {
        element: '.reports-page .crm-tour-btn',
        data: { id: 'replay' },
        popover: {
            title: 'Replay any time',
            description: 'You can start this tutorial again anytime from here.',
        },
    },
]

const money = (v) => {
    const n = Number(v) || 0
    return `$${Math.round(n).toLocaleString()}`
}
// The stored calendar date — the one the backend buckets by — never shifted
// into the viewer's timezone.
const shortDate = (iso) => {
    const d = parseDateOnly(String(iso || '').slice(0, 10))
    return d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'
}
const initials = (name) => {
    if (!name) return '?'
    const parts = name.trim().split(/\s+/)
    return (parts.length > 1 ? parts[0][0] + parts[1][0] : parts[0].slice(0, 2)).toUpperCase()
}

// The closed-deal row's customer, linked to their detail page. A real button
// rather than the row itself: only the name opens the person, and it reaches
// keyboard users without any handling of our own. Rows whose payload carries no
// person_id stay plain text — a link that resolves nowhere is worse than none.
const CustomerName = ({ deal, onOpen }) => {
    if (!deal.person_id) return deal.person_name
    return (
        <button type="button" className="cust-link" onClick={() => onOpen(deal.person_id)}>
            {deal.person_name}
        </button>
    )
}

// How many profit rows the deal has. The backend decides what a row is and
// whether all are paid, so this only renders what it sends: red while any row is
// unpaid, green once all are, and no badge for a deal with no rows or a backend
// that predates the fields. Lead History shows it too.
export const ProfitCount = ({ deal }) => {
    const count = deal.profit_item_count
    if (typeof count !== 'number' || !count) return null
    const rows = `${count} profit item${count === 1 ? '' : 's'}`
    const label = typeof deal.collected_item_count === 'number'
        ? `${rows}, ${deal.collected_item_count} collected`
        : rows
    const paidClass = deal.is_paid === true ? ' is-paid' : deal.is_paid === false ? ' is-unpaid' : ''
    return (
        <span className={`profit-count${paidClass}`} title={label} aria-label={label}>
            {count}
        </span>
    )
}

// The count and the amount, which opens the saved breakdown. Only the amount is
// the control — the row itself stays inert, and the customer name keeps its own.
const ProfitCell = ({ deal, onOpenProfit }) => (
    <span className="profit-cell">
        <ProfitCount deal={deal} />
        <button
            type="button"
            className="profit-link"
            aria-haspopup="dialog"
            onClick={(e) => onOpenProfit(e.currentTarget, deal.id)}
        >
            {money(deal.profit)}
        </button>
    </span>
)
const Bars = ({ rows, colorFor, empty }) => {
    const max = Math.max(1, ...rows.map((r) => r.profit))
    if (rows.length === 0) return <div className="rp-empty">{empty}</div>
    return rows.map((r, i) => (
        <div className="bar-row" key={i}>
            {r.avatar && <div className="bar-av" style={{ background: r.color }}>{r.avatar}</div>}
            <div className="bar-main">
                <div className="bar-name"><span>{r.label}</span><span className="bar-amt">{money(r.profit)}</span></div>
                <div className="bar-track"><div className="bar-fill" style={{ width: `${Math.round(r.profit / max * 100)}%`, background: colorFor(r, i) }} /></div>
            </div>
        </div>
    ))
}

export const ReportsPage = () => {
    const { profileState, isAccountAdmin } = useUserContext()
    const isMobile = useIsMobile()
    const openPerson = useOpenPerson()
    // Which deal's profit breakdown is open, and what it hangs off. One popover
    // for the table, re-anchored per click.
    const [profit, setProfit] = useState({ anchorEl: null, id: null })
    // Narrows the closed-deals list to what is still owed on. Page state, not
    // store state: only this list reads it, it has to outlive a period refetch,
    // and it should be gone the next time Reports is opened.
    const [outstandingOnly, setOutstandingOnly] = useState(false)
    const { mode, month, year, owner, data, loading, setControl, fetchReports } = useCrmReportsStore(
        useShallow((state) => ({
            mode: state.mode, month: state.month, year: state.year, owner: state.owner,
            data: state.data, loading: state.loading,
            setControl: state.setControl, fetchReports: state.fetchReports,
        }))
    )

    useEffect(() => {
        fetchReports()
    }, [])

    // The first-visit tour, over a loaded report. Read live — a warm store's
    // value at mount predates the fetch the effect above has just started — and
    // never over a first load that failed (no data).
    const tour = usePageTour({ key: 'reports', version: REPORTS_TOUR_VERSION, steps: REPORTS_TOUR_STEPS })
    useEffect(function autoStartTour() {
        const s = useCrmReportsStore.getState()
        if (s.loading || !s.data) return
        tour.autoStart()
    }, [loading])

    const accountUsers = profileState?.account_users || []
    // account_users is the whole account whatever this user's role, but a member's
    // report only ever covers their own won deals — `?owner=` naming a teammate
    // comes back empty, not forbidden. So the strip is gated on the role rather
    // than on how many people are on the account: otherwise every rep on a team
    // of two or more would get chips that silently produce a blank report.
    const showOwnerStrip = isAccountAdmin() && accountUsers.length > 1
    const periodLabel = labelForPeriod(mode, year, month)
    // What the second dropdown offers. `first_year` rides on the report itself,
    // so it is absent until the first response lands and narrows when a
    // salesperson is picked — the helper treats both as "just this year".
    const periodChoices = periodOptions(mode, { firstYear: data?.first_year, year, month })

    const kpis = data?.kpis || {}
    const salesRows = useMemo(
        () => (data?.by_salesperson || []).map((r) => ({ label: `${r.name} · ${r.count} deals`, profit: r.profit, avatar: initials(r.name), color: ownerColor(r.id), id: r.id })),
        [data]
    )
    const sourceRows = useMemo(
        () => (data?.by_source || []).map((r) => ({ label: r.label, profit: r.profit, source: r.source })),
        [data]
    )
    const leadSources = data?.lead_sources || []
    // Memoized for identity, not cost: `|| []` hands back a new array on every
    // render when the period has none, which would re-run visibleDeals each time.
    const deals = useMemo(() => data?.deals || [], [data])
    // is_paid is the backend's own settled/unsettled rule, so the list agrees
    // with the Outstanding KPI above it. Not `outstanding > 0`: profit can be
    // negative, and a row from a backend that never sent the field would fall
    // into the filter rather than out of it — hence the strict === false.
    const visibleDeals = useMemo(
        () => (outstandingOnly ? deals.filter((deal) => deal.is_paid === false) : deals),
        [deals, outstandingOnly]
    )
    const dealCountText = outstandingOnly
        ? `${visibleDeals.length} of ${deals.length} deals`
        : `${deals.length} deals`
    const emptyText = loading
        ? 'Loading…'
        : outstandingOnly
            ? 'No outstanding deals in this period.'
            : 'No deals closed in this period.'
    const overallRate = (() => {
        const t = leadSources.reduce((s, r) => s + r.leads_in, 0)
        const c = leadSources.reduce((s, r) => s + r.closed, 0)
        return t ? Math.round(c / t * 100) : 0
    })()

    const openProfit = (anchorEl, id) => setProfit({ anchorEl, id })
    // Both cleared together, so reopening a row asks for it again rather than
    // showing what it held last time.
    const closeProfit = () => setProfit({ anchorEl: null, id: null })
    // Every route to the filter lands here. Filtering can unmount the row a
    // breakdown is anchored to, which would leave the popover pinned to a
    // detached node, so it closes with the same click that changes the list —
    // including a click that re-picks the mode already showing.
    const selectOutstanding = (value) => {
        setOutstandingOnly(value)
        closeProfit()
    }
    // The segments name the mode they want; the KPI amount flips between them.
    const toggleOutstanding = () => selectOutstanding(!outstandingOnly)
    // The list the amount filters sits below the charts, so the amount also
    // brings it into view. The panel is always rendered and only its rows
    // change, so it can scroll straight from the click.
    const dealsRef = useRef(null)
    const toggleFromAmount = () => {
        toggleOutstanding()
        dealsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }

    return (
        <Box className="reports-page">
            <div className="toolbar">
                <span className="toolbar-title">Reports</span>
                {/* The salesperson filter shares this row rather than one of its own. */}
                {showOwnerStrip && (
                    <div className="rp-owners">
                        <span className="fs-label">Salesperson</span>
                        <span className={`rep-chip all${owner === 'all' ? ' active' : ''}`} onClick={() => setControl({ owner: 'all' })}>
                            <span className="rep-dot" style={{ background: '#7048cc' }}>★</span>Whole company
                        </span>
                        {accountUsers.map((u) => (
                            <span key={u.id} className={`rep-chip${owner === u.id ? ' active' : ''}`} onClick={() => setControl({ owner: u.id })}>
                                <span className="rep-dot" style={{ background: ownerColor(u.id) }}>{initials(u.username || u.email)}</span>
                                {u.username || u.email}
                            </span>
                        ))}
                    </div>
                )}
                <div className="toolbar-divider" />
                {/* Scope, then the period within it. */}
                <div className="rp-period">
                    <select
                        className="rp-select rp-scope"
                        aria-label="Report scope"
                        value={mode}
                        onChange={(e) => setControl(scopePatch(e.target.value, { month, year }))}
                    >
                        {SCOPES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                    {/* All Time has no period to pick, and a disabled select
                        would only invite a click that can't do anything. */}
                    {periodChoices.length > 0 && (
                        <select
                            className="rp-select"
                            aria-label="Report period"
                            value={selectedKey(mode, year, month)}
                            onChange={(e) => {
                                const picked = periodChoices.find((o) => o.value === e.target.value)
                                if (picked) setControl(picked.patch)
                            }}
                        >
                            {periodChoices.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                        </select>
                    )}
                </div>
                <button
                    type="button"
                    className="crm-tour-btn"
                    aria-label="Show Tutorial"
                    title="Show Tutorial"
                    onClick={tour.replay}
                >
                    <HelpOutlineIcon />
                    <span className="crm-tour-label">Show Tutorial</span>
                </button>
            </div>

            <div className="rp-body">
                <div className="kpis">
                    <div className="kpi"><div className="kpi-label">Deals Closed</div><div className="kpi-value">{kpis.deals_closed ?? 0}</div><div className="kpi-delta up">{owner === 'all' ? 'whole company' : 'selected rep'}</div></div>
                    <div className="kpi"><div className="kpi-label">Gross Profit</div><div className="kpi-value">{money(kpis.gross_profit)}</div></div>
                    <div className="kpi"><div className="kpi-label">Collected</div><div className="kpi-value">{money(kpis.collected)}</div></div>
                    <div className="kpi">
                        <div className="kpi-label">Outstanding</div>
                        <div className="kpi-value">
                            <button
                                type="button"
                                className="kpi-link"
                                aria-pressed={outstandingOnly}
                                aria-label={`Outstanding ${money(kpis.outstanding)}`}
                                onClick={toggleFromAmount}
                            >
                                {money(kpis.outstanding)}
                            </button>
                        </div>
                    </div>
                </div>

                <div className="grid2">
                    <div className="panel">
                        <div className="panel-head"><div className="panel-title">By Salesperson</div><div className="panel-sub">{periodLabel}</div></div>
                        <div className="panel-body"><Bars rows={salesRows} colorFor={(r) => r.color} empty={loading ? 'Loading…' : 'No deals closed.'} /></div>
                    </div>
                    <div className="panel">
                        <div className="panel-head"><div className="panel-title">Where deals came from</div><div className="panel-sub">{periodLabel}</div></div>
                        <div className="panel-body"><Bars rows={sourceRows} colorFor={(r) => (r.source === 'RENEWAL' ? '#d07020' : '#4a90e2')} empty={loading ? 'Loading…' : '—'} /></div>
                    </div>
                </div>

                <div className="panel">
                    <div className="panel-head">
                        <div><div className="panel-title">Lead Sources &amp; Close Rate</div>
                            <div className="panel-desc">Where people came in from, and how often each source becomes a sale (all-time).</div></div>
                        <div className="panel-sub">{overallRate}% closed overall</div>
                    </div>
                    <div className="panel-body flush">
                        {isMobile ? (
                            <div className="rp-cards">
                                {leadSources.map((r) => {
                                    const c = r.close_rate >= 50 ? '#2e9967' : r.close_rate >= 25 ? '#d07020' : '#c93535'
                                    return (
                                        <div className="rp-card" key={r.source}>
                                            <div className="rp-card-main">
                                                <div className="rp-card-name">{r.label}</div>
                                                <div className="rp-card-sub">{r.leads_in} in · {r.closed} closed</div>
                                                <div className="bar-track" style={{ marginTop: 7 }}><div className="bar-fill" style={{ width: `${r.close_rate}%`, background: c }} /></div>
                                            </div>
                                            <div className="rp-card-right"><b style={{ color: c }}>{r.close_rate}%</b></div>
                                        </div>
                                    )
                                })}
                                {leadSources.length === 0 && <div className="rp-empty">No lead data.</div>}
                            </div>
                        ) : (
                        <table>
                            <thead><tr><th>Lead Source</th><th className="r">Leads In</th><th className="r">Closed</th><th className="r">Close Rate</th><th>Conversion</th></tr></thead>
                            <tbody>
                                {leadSources.map((r) => {
                                    const c = r.close_rate >= 50 ? '#2e9967' : r.close_rate >= 25 ? '#d07020' : '#c93535'
                                    return (
                                        <tr key={r.source}>
                                            <td>{r.label}</td>
                                            <td className="r">{r.leads_in}</td>
                                            <td className="r profit">{r.closed}</td>
                                            <td className="r"><b style={{ color: c }}>{r.close_rate}%</b></td>
                                            <td><div className="bar-track" style={{ maxWidth: 170 }}><div className="bar-fill" style={{ width: `${r.close_rate}%`, background: c }} /></div></td>
                                        </tr>
                                    )
                                })}
                                {leadSources.length === 0 && <tr><td colSpan={5}><div className="rp-empty">No lead data.</div></td></tr>}
                            </tbody>
                        </table>
                        )}
                    </div>
                </div>

                <div className="panel" ref={dealsRef}>
                    <div className="panel-head deals-head">
                        <div className="panel-title">Deals closed · {periodLabel}</div>
                        <div className="panel-sub">{dealCountText}</div>
                        <div className="rp-seg" role="group" aria-label="Filter deals by amount owed">
                            <button
                                type="button"
                                className={outstandingOnly ? '' : 'on'}
                                aria-pressed={!outstandingOnly}
                                onClick={() => selectOutstanding(false)}
                            >
                                All
                            </button>
                            <button
                                type="button"
                                className={outstandingOnly ? 'on' : ''}
                                aria-pressed={outstandingOnly}
                                onClick={() => selectOutstanding(true)}
                            >
                                Outstanding
                            </button>
                        </div>
                    </div>
                    <div className="panel-body flush">
                        {isMobile ? (
                            <div className="rp-cards">
                                {visibleDeals.map((d) => (
                                    <div className="rp-card" key={d.id}>
                                        <span className="rp-card-av" style={{ background: ownerColor(d.owner_id) }}>{initials(d.owner_name)}</span>
                                        <div className="rp-card-main">
                                            <div className="rp-card-name"><CustomerName deal={d} onOpen={openPerson} /></div>
                                            <div className="rp-card-sub">{[d.source_label, d.dealership, shortDate(d.closed_date)].filter(Boolean).join(' · ')}</div>
                                        </div>
                                        <div className="rp-card-right profit">
                                            <ProfitCell deal={d} onOpenProfit={openProfit} />
                                        </div>
                                    </div>
                                ))}
                                {visibleDeals.length === 0 && <div className="rp-empty">{emptyText}</div>}
                            </div>
                        ) : (
                        <table>
                            <thead><tr><th>Customer</th><th>Salesperson</th><th>Source</th><th>Closed</th><th>Dealership</th><th className="profit-head">Profit</th></tr></thead>
                            <tbody>
                                {visibleDeals.map((d) => (
                                    <tr key={d.id}>
                                        <td className="cust"><CustomerName deal={d} onOpen={openPerson} /></td>
                                        <td className="rep">
                                            <span className="rep-tag" title={d.owner_name}>
                                                <span className="rep-mini" style={{ background: ownerColor(d.owner_id) }}>{initials(d.owner_name)}</span>
                                                <span className="rep-name">{d.owner_name}</span>
                                            </span>
                                        </td>
                                        <td>{d.source_label}</td>
                                        <td>{shortDate(d.closed_date)}</td>
                                        <td className="dealership"><span>{d.dealership || '—'}</span></td>
                                        <td className="profit"><ProfitCell deal={d} onOpenProfit={openProfit} /></td>
                                    </tr>
                                ))}
                                {visibleDeals.length === 0 && <tr><td colSpan={6}><div className="rp-empty">{emptyText}</div></td></tr>}
                            </tbody>
                        </table>
                        )}
                    </div>
                </div>
            </div>

            <ReportsProfitPopover
                opportunityId={profit.id}
                anchorEl={profit.anchorEl}
                onClose={closeProfit}
                onSaved={fetchReports}
            />
        </Box>
    )
}
